import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { toast } from "@/components/ui/sonner";
import { ResponsiveDialog } from "@/components/common/ResponsiveDialog";
import { DecimalInput, HoursHint } from "@/components/common/DecimalInput";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage, isConflict, newIdempotencyKey } from "@/lib/api";
import { fmtMinutes, fmtNumber, parseDecimalInput, previewMinutes, todayIso, toInputDecimal } from "@/lib/format";
import { UNITS, unitShort } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const NONE = "__ingen__";

function useEntrySave({ order, entry, onOpenChange, onSaved }) {
  const queryClient = useQueryClient();
  const keyRef = useRef(newIdempotencyKey());
  const [error, setError] = useState(null);

  const mutation = useMutation({
    mutationFn: async (payload) => {
      if (entry) {
        return (await api.put(`/entries/${entry.id}`, { ...payload, version: entry.version })).data;
      }
      return (await api.post(`/orders/${order.id}/entries`, { ...payload, idempotency_key: keyRef.current })).data;
    },
    onSuccess: (data) => {
      keyRef.current = newIdempotencyKey();
      queryClient.invalidateQueries({ queryKey: ["order", order.id] });
      queryClient.invalidateQueries({ queryKey: ["entries", order.id] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["counts"] });
      queryClient.invalidateQueries({ queryKey: ["history", order.id] });
      onSaved?.(data);
      onOpenChange(false);
    },
    onError: (err) => {
      // Skjemaet beheld verdiane sine; brukaren kan rette og prøve igjen
      setError({ message: errorMessage(err), conflict: isConflict(err) });
    },
  });

  const resetKey = () => {
    keyRef.current = newIdempotencyKey();
    setError(null);
  };
  return { mutation, error, setError, resetKey };
}

const ErrorBox = ({ error, testId }) =>
  error ? (
    <Alert variant="destructive" data-testid={testId} className="bg-card">
      <AlertTriangle className="h-4 w-4" />
      <AlertDescription>{error.message}{error.conflict ? " Last inn ordra på nytt og prøv igjen." : ""}</AlertDescription>
    </Alert>
  ) : null;

const ForUserSelect = ({ order, value, onChange }) => {
  const { isAdmin } = useAuth();
  if (!isAdmin || !order.assigned_users?.length) return null;
  return (
    <div className="space-y-2">
      <Label>Gjeld medarbeidar</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger  data-testid="entry-for-user-select"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-popover">
          {order.assigned_users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
};

/* ---------------- Før timar ---------------- */
export const ForTimarDialog = ({ open, onOpenChange, order, entry = null, onSaved }) => {
  const { user } = useAuth();
  const { mutation, error, resetKey } = useEntrySave({ order, entry, onOpenChange, onSaved });
  const [hours, setHours] = useState("");
  const [date, setDate] = useState(todayIso());
  const [itemId, setItemId] = useState(NONE);
  const [resourceId, setResourceId] = useState(NONE);
  const [note, setNote] = useState("");
  const [forUser, setForUser] = useState("");

  const services = useQuery({ queryKey: ["catalog", "teneste"], queryFn: async () => (await api.get("/catalog", { params: { category: "teneste" } })).data, enabled: open });
  const resources = useQuery({ queryKey: ["resources"], queryFn: async () => (await api.get("/resources")).data, enabled: open });

  useEffect(() => {
    if (!open) return;
    resetKey();
    setHours(entry ? toInputDecimal(entry.hours) : "");
    setDate(entry?.date || todayIso());
    setItemId(entry?.catalog_item_id || order.default_hour_item_id || NONE);
    setResourceId(entry?.resource_id || NONE);
    setNote(entry?.note || "");
    setForUser(entry?.user_id || order.responsible_user?.id || order.assigned_users?.[0]?.id || user?.id || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entry?.id]);

  const n = parseDecimalInput(hours);
  const valid = n !== null && !Number.isNaN(n) && n > 0 && n <= 24;

  const submit = (e) => {
    e?.preventDefault();
    if (!valid) return;
    const payload = {
      type: "timar",
      hours,
      date,
      catalog_item_id: itemId === NONE ? null : itemId,
      resource_id: resourceId === NONE ? null : resourceId,
      note: note || null,
    };
    if (!entry && forUser) payload.user_id = forUser;
    mutation.mutate(payload, {
      onSuccess: (data) => {
        toast.success(entry ? "Timar oppdatert" : "Timar lagra", { description: `${fmtNumber(data.hours, 2)} t (${fmtMinutes(data.minutes)})${data.catalog_item_name ? ` – ${data.catalog_item_name}` : ""}` });
      },
    });
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      title={entry ? "Endre timar" : "Før timar"}
      description={`${order.order_number} · ${order.title}`}
      testId="for-timar-dialog"
      footer={
        <>
          <Button type="button" variant="outline" className="h-11 bg-card md:h-10" onClick={() => onOpenChange(false)} data-testid="for-timar-avbryt">Avbryt</Button>
          <Button type="submit" form="for-timar-form" className="h-11 md:h-10" disabled={!valid || mutation.isPending} data-testid="for-timar-lagre">
            {mutation.isPending ? "Lagrar …" : "Lagre"}
          </Button>
        </>
      }
    >
      <form id="for-timar-form" onSubmit={submit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="for-timar-varigheit">Varigheit (timar)</Label>
          <DecimalInput id="for-timar-varigheit" value={hours} onChange={setHours} placeholder="t.d. 1,5" autoFocus data-testid="for-timar-varigheit" aria-invalid={hours !== "" && !valid} />
          <HoursHint value={hours} testId="for-timar-minutt-hint" />
        </div>
        <div className="space-y-2">
          <Label>Type arbeid</Label>
          <Select value={itemId} onValueChange={setItemId}>
            <SelectTrigger  data-testid="for-timar-teneste"><SelectValue placeholder="Vel type arbeid" /></SelectTrigger>
            <SelectContent className="bg-popover">
              <SelectItem value={NONE}>Ikkje vald</SelectItem>
              {(services.data || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="for-timar-dato">Dato</Label>
            <Input id="for-timar-dato" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="tabular" data-testid="for-timar-dato" required />
          </div>
          <div className="space-y-2">
            <Label>Køyretøy / maskin (valfritt)</Label>
            <Select value={resourceId} onValueChange={setResourceId}>
              <SelectTrigger  data-testid="for-timar-ressurs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover">
                <SelectItem value={NONE}>Ingen</SelectItem>
                {(resources.data || []).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {!entry ? <ForUserSelect order={order} value={forUser} onChange={setForUser} /> : null}
        <div className="space-y-2">
          <Label htmlFor="for-timar-merknad">Merknad (valfritt)</Label>
          <Textarea id="for-timar-merknad" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="bg-card" data-testid="for-timar-merknad" />
        </div>
        <ErrorBox error={error} testId="for-timar-error" />
      </form>
    </ResponsiveDialog>
  );
};

/* ---------------- Ressursbruk ---------------- */
export const RessursbrukDialog = ({ open, onOpenChange, order, entry = null, onSaved }) => {
  const { user } = useAuth();
  const { mutation, error, resetKey } = useEntrySave({ order, entry, onOpenChange, onSaved });
  const [hours, setHours] = useState("");
  const [date, setDate] = useState(todayIso());
  const [resourceId, setResourceId] = useState("");
  const [note, setNote] = useState("");
  const [forUser, setForUser] = useState("");
  const resources = useQuery({ queryKey: ["resources"], queryFn: async () => (await api.get("/resources")).data, enabled: open });

  useEffect(() => {
    if (!open) return;
    resetKey();
    setHours(entry ? toInputDecimal(entry.hours) : "");
    setDate(entry?.date || todayIso());
    setResourceId(entry?.resource_id || "");
    setNote(entry?.note || "");
    setForUser(entry?.user_id || order.responsible_user?.id || user?.id || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entry?.id]);

  const n = parseDecimalInput(hours);
  const valid = resourceId && n !== null && !Number.isNaN(n) && n > 0 && n <= 24;
  const selected = (resources.data || []).find((r) => r.id === resourceId);

  const submit = (e) => {
    e?.preventDefault();
    if (!valid) return;
    const payload = { type: "ressurs", hours, date, resource_id: resourceId, note: note || null };
    if (!entry && forUser) payload.user_id = forUser;
    mutation.mutate(payload, { onSuccess: (data) => toast.success(entry ? "Ressursbruk oppdatert" : "Ressursbruk lagra", { description: `${data.resource_name} – ${fmtNumber(data.hours, 2)} t` }) });
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      title={entry ? "Endre køyretøy/maskin" : "Registrer køyretøy/maskin"}
      description={`${order.order_number} · ${order.title}`}
      testId="ressurs-dialog"
      footer={
        <>
          <Button type="button" variant="outline" className="h-11 bg-card md:h-10" onClick={() => onOpenChange(false)} data-testid="ressurs-avbryt">Avbryt</Button>
          <Button type="submit" form="ressurs-form" className="h-11 md:h-10" disabled={!valid || mutation.isPending} data-testid="ressurs-lagre">{mutation.isPending ? "Lagrar …" : "Lagre"}</Button>
        </>
      }
    >
      <form id="ressurs-form" onSubmit={submit} className="space-y-4">
        <div className="space-y-2">
          <Label>Køyretøy / maskin</Label>
          <Select value={resourceId} onValueChange={setResourceId}>
            <SelectTrigger  data-testid="ressurs-select"><SelectValue placeholder="Vel ressurs" /></SelectTrigger>
            <SelectContent className="bg-popover">
              {(resources.data || []).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {selected?.includes_driver ? <p className="text-xs text-muted-foreground" data-testid="ressurs-includes-driver-hint">Satsen for denne ressursen inkluderer førar. Førartimar på same dag blir ikkje fakturerte dobbelt.</p> : null}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="ressurs-timar">Timar i bruk</Label>
            <DecimalInput id="ressurs-timar" value={hours} onChange={setHours} placeholder="t.d. 2" data-testid="ressurs-timar" />
            <HoursHint value={hours} testId="ressurs-minutt-hint" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ressurs-dato">Dato</Label>
            <Input id="ressurs-dato" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="tabular" data-testid="ressurs-dato" required />
          </div>
        </div>
        {!entry ? <ForUserSelect order={order} value={forUser} onChange={setForUser} /> : null}
        <div className="space-y-2">
          <Label htmlFor="ressurs-merknad">Merknad (valfritt)</Label>
          <Textarea id="ressurs-merknad" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="bg-card" data-testid="ressurs-merknad" />
        </div>
        <ErrorBox error={error} testId="ressurs-error" />
      </form>
    </ResponsiveDialog>
  );
};

/* ---------------- Legg til varer ---------------- */
export const LeggTilVarerDialog = ({ open, onOpenChange, order, entry = null, onSaved }) => {
  const { mutation, error, resetKey } = useEntrySave({ order, entry, onOpenChange, onSaved });
  const [itemId, setItemId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [unit, setUnit] = useState("");
  const [date, setDate] = useState(todayIso());
  const [note, setNote] = useState("");
  const catalog = useQuery({ queryKey: ["catalog", "varer"], queryFn: async () => (await api.get("/catalog", { params: { category: "masse,vare,ressurs" } })).data, enabled: open });

  useEffect(() => {
    if (!open) return;
    resetKey();
    setItemId(entry?.catalog_item_id || "");
    setQuantity(entry ? toInputDecimal(entry.quantity) : "");
    setUnit(entry?.unit || "");
    setDate(entry?.date || todayIso());
    setNote(entry?.note || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entry?.id]);

  const items = catalog.data || [];
  const selected = items.find((i) => i.id === itemId);
  const q = parseDecimalInput(quantity);
  const valid = itemId && q !== null && !Number.isNaN(q) && q > 0;
  const unitOptions = UNITS.some((u) => u.value === (unit || selected?.unit)) ? UNITS : [...UNITS, { value: unit || selected?.unit, label: unit || selected?.unit }];

  const choose = (id) => {
    setItemId(id);
    const it = items.find((i) => i.id === id);
    if (it) setUnit(it.unit);
  };

  const submit = (e) => {
    e?.preventDefault();
    if (!valid) return;
    mutation.mutate(
      { type: "vare", catalog_item_id: itemId, quantity, unit: unit || selected?.unit || "stk", date, note: note || null },
      { onSuccess: (data) => toast.success(entry ? "Vare oppdatert" : "Vare lagt til", { description: `${fmtNumber(data.quantity, 3)} ${unitShort(data.unit)} ${data.catalog_item_name}` }) },
    );
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      title={entry ? "Endre vare" : "Legg til varer"}
      description={`${order.order_number} · ${order.title}`}
      testId="legg-til-varer-dialog"
      footer={
        <>
          <Button type="button" variant="outline" className="h-11 bg-card md:h-10" onClick={() => onOpenChange(false)} data-testid="legg-til-varer-avbryt">Avbryt</Button>
          <Button type="submit" form="legg-til-varer-form" className="h-11 md:h-10" disabled={!valid || mutation.isPending} data-testid="legg-til-varer-submit">{mutation.isPending ? "Lagrar …" : entry ? "Lagre" : "Legg til"}</Button>
        </>
      }
    >
      <form id="legg-til-varer-form" onSubmit={submit} className="space-y-4">
        <div className="space-y-2">
          <Label>Vare</Label>
          {selected ? (
            <div className="flex items-center justify-between rounded-md border border-border bg-muted px-3 py-2" data-testid="legg-til-varer-valt">
              <span className="text-sm font-medium">{selected.name} <span className="text-muted-foreground">({unitShort(selected.unit)})</span></span>
              <Button type="button" variant="ghost" size="sm" className="h-9" onClick={() => setItemId("")} data-testid="legg-til-varer-byt">Byt</Button>
            </div>
          ) : (
            <Command className="rounded-md border border-border bg-card" data-testid="varer-katalog-combobox">
              <CommandInput placeholder="Søk i katalogen …" autoFocus data-testid="legg-til-varer-sok" />
              <CommandList className="max-h-56">
                <CommandEmpty>{catalog.isLoading ? "Lastar …" : "Ingen treff"}</CommandEmpty>
                <CommandGroup>
                  {items.map((it) => (
                    <CommandItem key={it.id} value={`${it.name} ${it.sku || ""} ${it.unit}`} onSelect={() => choose(it.id)} className="min-h-[44px] cursor-pointer" data-testid="legg-til-varer-vare">
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{it.name}</span>
                        {it.sku ? <span className="num block text-[11px] text-muted-foreground">{it.sku}</span> : null}
                      </span>
                      <span className="text-xs text-muted-foreground">{unitShort(it.unit)}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          )}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="legg-til-varer-mengde">Mengde</Label>
            <DecimalInput id="legg-til-varer-mengde" value={quantity} onChange={setQuantity} placeholder="t.d. 12" data-testid="legg-til-varer-mengde" aria-invalid={quantity !== "" && (Number.isNaN(q) || q <= 0)} />
            {quantity !== "" && (Number.isNaN(q) || q <= 0) ? <p className="text-xs text-destructive">Ugyldig mengde. Bruk t.d. 12 eller 1,5.</p> : <p className="text-xs text-muted-foreground">Komma som desimalteikn, t.d. 1,5</p>}
          </div>
          <div className="space-y-2">
            <Label>Eining</Label>
            <Select value={unit || selected?.unit || ""} onValueChange={setUnit}>
              <SelectTrigger  data-testid="legg-til-varer-eining"><SelectValue placeholder="Eining" /></SelectTrigger>
              <SelectContent className="bg-popover">
                {unitOptions.filter((u) => u.value).map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="legg-til-varer-dato">Dato</Label>
            <Input id="legg-til-varer-dato" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="tabular" data-testid="legg-til-varer-dato" required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="legg-til-varer-merknad">Merknad (valfritt)</Label>
            <Input id="legg-til-varer-merknad" value={note} onChange={(e) => setNote(e.target.value)} data-testid="legg-til-varer-merknad" />
          </div>
        </div>
        <ErrorBox error={error} testId="legg-til-varer-error" />
      </form>
    </ResponsiveDialog>
  );
};

/* ---------------- Merknad ---------------- */
export const MerknadDialog = ({ open, onOpenChange, order, entry = null, onSaved }) => {
  const { mutation, error, resetKey } = useEntrySave({ order, entry, onOpenChange, onSaved });
  const [note, setNote] = useState("");
  useEffect(() => {
    if (!open) return;
    resetKey();
    setNote(entry?.note || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entry?.id]);
  const valid = note.trim().length > 0;
  const submit = (e) => {
    e?.preventDefault();
    if (!valid) return;
    mutation.mutate({ type: "merknad", note: note.trim(), date: todayIso() }, { onSuccess: () => toast.success(entry ? "Merknad oppdatert" : "Merknad lagra") });
  };
  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      title={entry ? "Endre merknad" : "Ny merknad"}
      description={`${order.order_number} · ${order.title}`}
      testId="merknad-dialog"
      footer={
        <>
          <Button type="button" variant="outline" className="h-11 bg-card md:h-10" onClick={() => onOpenChange(false)} data-testid="merknad-avbryt">Avbryt</Button>
          <Button type="submit" form="merknad-form" className="h-11 md:h-10" disabled={!valid || mutation.isPending} data-testid="merknad-lagre">{mutation.isPending ? "Lagrar …" : "Lagre"}</Button>
        </>
      }
    >
      <form id="merknad-form" onSubmit={submit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="merknad-tekst">Merknad</Label>
          <Textarea id="merknad-tekst" value={note} onChange={(e) => setNote(e.target.value)} rows={4} autoFocus className="bg-card" data-testid="merknad-tekst" />
        </div>
        <ErrorBox error={error} testId="merknad-error" />
      </form>
    </ResponsiveDialog>
  );
};

/* ---------------- Meld utført ---------------- */
export const MeldUtfortDialog = ({ open, onOpenChange, order, onDone }) => {
  const queryClient = useQueryClient();
  const [note, setNote] = useState("");
  const [error, setError] = useState(null);
  useEffect(() => {
    if (open) {
      setNote("");
      setError(null);
    }
  }, [open]);
  const mutation = useMutation({
    mutationFn: async () => (await api.post(`/orders/${order.id}/status`, { action: "meld_utfort", note: note || null, version: order.version })).data,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["order", order.id] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["counts"] });
      queryClient.invalidateQueries({ queryKey: ["history", order.id] });
      toast.success("Ordra er meld utført", { description: `${order.order_number} går no til kontroll hos administrasjonen.` });
      onDone?.(data);
      onOpenChange(false);
    },
    onError: (err) => setError({ message: errorMessage(err), conflict: isConflict(err) }),
  });
  const stats = order.stats || {};
  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Meld utført"
      description="Etter dette kan du ikkje endre registreringane sjølv. Administrasjonen tek over kontrollen."
      testId="meld-utfort-dialog"
      footer={
        <>
          <Button type="button" variant="outline" className="h-11 bg-card md:h-10" onClick={() => onOpenChange(false)} data-testid="meld-utfort-avbryt">Avbryt</Button>
          <Button type="button" className="h-11 md:h-10" onClick={() => mutation.mutate()} disabled={mutation.isPending} data-testid="meld-utfort-bekreft">{mutation.isPending ? "Sender …" : "Meld utført"}</Button>
        </>
      }
    >
      <div className="grid grid-cols-3 gap-2 rounded-lg border border-border bg-muted p-3 text-center text-sm" data-testid="meld-utfort-oppsummering">
        <div><p className="num text-lg font-semibold">{fmtMinutes(stats.minutes || 0)}</p><p className="text-xs text-muted-foreground">Timar</p></div>
        <div><p className="num text-lg font-semibold">{fmtMinutes(stats.resource_minutes || 0)}</p><p className="text-xs text-muted-foreground">Køyretøy/maskin</p></div>
        <div><p className="num text-lg font-semibold">{stats.goods_count || 0}</p><p className="text-xs text-muted-foreground">Varelinjer</p></div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="meld-utfort-merknad">Merknad til administrasjonen (valfritt)</Label>
        <Textarea id="meld-utfort-merknad" value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="bg-card" data-testid="meld-utfort-merknad" />
      </div>
      <ErrorBox error={error} testId="meld-utfort-error" />
    </ResponsiveDialog>
  );
};

export const entryTypeClass = (type) =>
  cn("inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md", {
    "bg-secondary text-primary": type === "timar",
    "bg-secondary text-warning": type === "ressurs",
    "bg-secondary text-success": type === "vare",
    "bg-secondary text-muted-foreground": type === "merknad",
  });

export { previewMinutes };
